import './Viewport.scss';
import './Viewport.m.scss';
import { Component } from '@components/Component/Component.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { capitalizeFirstLetter } from '@components/Utility/index.js';
/**
 * Class `Viewport` extends from the `Component` class.
 * It represents a viewport where screens are managed.
 */
export class Viewport extends Component {
    /**
     * The ScreenManager used to manage screens within the Viewport.
     */
    get ScreenManager() {
        if (!this.dependencies.ScreenManager) {
            // we didnt receive a request dependency injection, so just use the global singleton
            return DIContainer.getInstance('ScreenManager');
            // this.dependencies.ScreenManager = // dont set the dependency in this case because screen manager can be swapped out.
        }
        return this.dependencies.ScreenManager;
    }
    /**
     * The constructor for the Viewport.
     * @param {string} id - The id of the Viewport.
     * @param {HTMLElement | null} rootElement - The root HTML element for the Viewport.
     * @param dependencies
     */
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        /**
         * The name of the component.
         */
        this.componentName = 'Viewport';
    }
    /**
     * This method is called to draw the viewport on the screen.
     * It also appends the ScreenManager's root element to the viewport's root element,
     * and instructs the ScreenManager to draw itself.
     */
    draw() {
        super.draw();
        this.rootElement.appendChild(this.ScreenManager.rootElement);
        this.ScreenManager.draw();
    }
    /**
     * Opens a screen in the viewport.
     * @param {string} url - The URL of the screen to open.
     */
    open(url) {
        this.ScreenManager.open(url);
    }
    updateScreenMode(mode) {
        if (mode) {
            const screenManagerOld = this.ScreenManager;
            // overwrite our singleton with the new mode
            DIContainer.registerSingleton('ScreenManager', () => {
                return DIContainer.getInstance(`ScreenManager_${capitalizeFirstLetter(mode)}`);
            });
            // now we can load the new singleton
            const screenManagerNew = this.ScreenManager;
            if (!screenManagerNew) {
                console.error(`Missing dependency ScreenManager_${capitalizeFirstLetter(mode)}`);
                return;
            }
            if (screenManagerOld === screenManagerNew) {
                return;
            }
            screenManagerOld.rootElement.remove();
            this.rootElement.appendChild(screenManagerNew.rootElement);
            if (screenManagerOld.drawn && !screenManagerNew.drawn) {
                screenManagerNew.draw();
            }
            // now we need to bring over the old forms to the new screen manager 
            screenManagerOld.forEachScreen((screen) => {
                const form = screen.unloadForm();
                if (form) {
                    screenManagerNew.open(form, { skipRefreshData: true });
                    form.screenRootMoved();
                }
                screenManagerOld.screenRemove(screen.id);
                screen.destroy();
            });
        }
    }
    /**
     * This method creates the HTML structure of the component.
     * @returns A string of HTML.
     */
    template() {
        return '';
    }
}
/**
 * The version of `Viewport`.
 */
Viewport.VERSION = '$Revision: 124621 $';
